// The single source of design tokens. `pnpm --filter @paige/ui build` renders it to
// src/styles/tokens.css; nothing else defines a colour, a spacing step or a radius.
//
// Visual direction (CapturePoint interface principles, "Approved visual direction"): neutral
// charcoal, white and grey surfaces; one restrained accent (Paige blue); larger, clearer type. Dark mode
// is designed, not inverted: depth and state stay distinguishable on charcoal.

export const semanticRoles = [
  'canvas',
  'surface',
  'surfaceRaised',
  'border',
  'text',
  'muted',
  'accent',
  'accentText',
  'accentStrong',
  'accentStrongText',
  'link',
  'danger',
  'success',
  'warning',
  'focus',
] as const;

export type SemanticRole = (typeof semanticRoles)[number];
export type ThemeName = 'light' | 'dark';

/** OKLCH triplets as strings so the accent can be derived from a hue at runtime. `accentStrong` is the
 * accent as a surface to stand on (the primary button), deep enough for white text in both themes. */
export type Palette = Record<SemanticRole, string>;

/**
 * The accent in a theme: its lightness and chroma, the person's hue (0-360) and how much of the chroma their colour
 * carries (0-1: a black or white pick is a neutral, Paige blue all of it). One accent colour for the whole product
 * (Dustin, 2026-10-20): the colour picked in the workbench's Appearance sets both (ThemeProvider `setAccent`).
 */
const tinted = (lightness: string, chroma: number): string =>
  `oklch(${lightness} calc(${chroma} * var(--paige-accent-chroma)) var(--paige-accent-hue))`;

/** The per-user accent is a hue and a share of chroma; lightness and chroma come from the theme. Default: the Paige electric blue of the logo mark (Dustin, 2026-09-18: never orange). */
export const accentDefaults = {
  hue: 232,
  chroma: 1,
  light: tinted('66%', 0.17),
  dark: tinted('75%', 0.15),
} as const;

export const palettes: Record<ThemeName, Palette> = {
  light: {
    canvas: 'oklch(97.5% 0 0)',
    surface: 'oklch(100% 0 0)',
    surfaceRaised: 'oklch(95.5% 0 0)',
    border: 'oklch(88% 0 0)',
    text: 'oklch(20% 0 0)',
    muted: 'oklch(42% 0 0)',
    accent: accentDefaults.light,
    accentText: 'oklch(16% 0 0)',
    accentStrong: tinted('46%', 0.14),
    accentStrongText: 'oklch(99% 0 0)',
    link: tinted('46%', 0.15),
    danger: 'oklch(48% 0.19 27)',
    success: 'oklch(46% 0.13 150)',
    warning: 'oklch(50% 0.12 80)',
    focus: tinted('58%', 0.18),
  },
  dark: {
    canvas: 'oklch(16% 0 0)',
    surface: 'oklch(21% 0 0)',
    surfaceRaised: 'oklch(27% 0 0)',
    border: 'oklch(34% 0 0)',
    text: 'oklch(94% 0 0)',
    muted: 'oklch(70% 0 0)',
    accent: accentDefaults.dark,
    accentText: 'oklch(14% 0 0)',
    accentStrong: tinted('54%', 0.16),
    accentStrongText: 'oklch(99% 0 0)',
    link: tinted('80%', 0.13),
    danger: 'oklch(72% 0.18 25)',
    success: 'oklch(76% 0.14 150)',
    warning: 'oklch(82% 0.14 85)',
    focus: tinted('78%', 0.15),
  },
};

/** Job lifecycle states (docs/ui-concept.md) map to state colours, not to raw shades. */
export const jobStates = [
  'setup',
  'teaching',
  'supervised',
  'automatic',
  'paused',
  'archived',
] as const;
export type JobState = (typeof jobStates)[number];

export const stateColors: Record<ThemeName, Record<JobState, string>> = {
  light: {
    setup: 'oklch(52% 0.13 300)',
    teaching: 'oklch(52% 0.14 250)',
    supervised: 'oklch(52% 0.14 60)',
    automatic: 'oklch(46% 0.13 150)',
    paused: 'oklch(48% 0.19 27)',
    archived: 'oklch(58% 0.02 250)',
  },
  dark: {
    setup: 'oklch(76% 0.12 300)',
    teaching: 'oklch(76% 0.12 250)',
    supervised: 'oklch(80% 0.14 65)',
    automatic: 'oklch(76% 0.14 150)',
    paused: 'oklch(72% 0.18 25)',
    archived: 'oklch(62% 0.02 250)',
  },
};

/** The staff band: a distinct accent so an admin screenshot is never mistaken for a customer's. */
export const staffAccent = { light: 'oklch(42% 0.12 255)', dark: 'oklch(74% 0.11 255)' } as const;

export const spacing = {
  0: '0',
  1: '0.25rem',
  2: '0.5rem',
  3: '0.75rem',
  4: '1rem',
  5: '1.5rem',
  6: '2rem',
  7: '3rem',
  8: '4rem',
} as const;
export const radius = {
  sm: '0.25rem',
  md: '0.5rem',
  lg: '0.75rem',
  xl: '1rem',
  full: '9999px',
} as const;

export const fonts = {
  body: "'Inter', 'Inter Fallback', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  heading:
    "'Barlow Condensed', 'Barlow Condensed Fallback', 'Arial Narrow', 'Helvetica Neue', sans-serif",
  mono: "ui-monospace, 'Cascadia Mono', 'Segoe UI Mono', Menlo, monospace",
} as const;

/** A real scale: body 16 px; headings step up in Barlow Condensed, which needs the extra size. */
export const fontSize = {
  xs: '0.75rem',
  sm: '0.875rem',
  md: '1rem',
  lg: '1.25rem',
  xl: '1.625rem',
  '2xl': '2.25rem',
  '3xl': '3rem',
} as const;
export const shadow = {
  sm: '0 1px 2px oklch(0% 0 0 / 0.06)',
  md: '0 6px 20px oklch(0% 0 0 / 0.14)',
} as const;
export const motion = {
  fast: '120ms',
  base: '200ms',
  easing: 'cubic-bezier(0.2, 0, 0, 1)',
} as const;
